<template>
  <div class="col">
    <spinner v-if="busy" />
    <template v-else>
      <template v-if="hasLeads">
        <div class="row justify-content-center">
          <b-card
            v-for="lead in leads"
            :key="lead.id"
            :body-class="'overflow-hidden border shadow rounded'"
            class="col-12 col-md-9 lead-card cursor-pointer overflow-hidden" @click="openLead(lead)"
          >
            <b-card-header>
              <b-row class="position-relative align-items-center">
                <lead-status-icons
                  class="position-absolute align-items-center"
                  :status-icons="lead.statusIcons"
                  :archived="(lead.dashboard_lead) ? lead.dashboard_lead.archived : false"
                />
                <b-col class="col-12 align-items-center d-flex flex-column px-0 text-center">
                  <h2 class="h3 m-auto text-truncate worktype-title">
                    {{ workTypeTranslated(lead.enquiry) }}
                  </h2>
                  <h3 class="mb-0">
                    {{ leadFullAddress(lead.enquiry.town, lead.enquiry.postcode, lead.address1, lead.address2) }}
                  </h3>
                </b-col>
              </b-row>
            </b-card-header>
            <b-card-body class="details fadein-details p-4">
              <b-row>
                <b-col class="col-8">
                  <b-row>
                    <b-col>
                      <div class="details__type">
                        {{ $t('Name:') }}
                      </div>
                      <div class="details__description">
                        {{ $t('fullName', {title: lead.enquiry.title, forename: lead.enquiry.forename, surname: lead.enquiry.surname }) }}
                      </div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <div class="details__type">
                        {{ $t('Telephone:') }}
                      </div>
                      <div class="details__description">
                        {{ lead.enquiry.telephone }}
                      </div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <div class="details__type">
                        {{ $t('Date:') }}
                      </div>
                      <div class="details__description">
                        {{ getReleaseDate(lead) | parseDate }} {{ getReleaseDate(lead) | parseTime }}
                      </div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <div class="details__type">
                        {{ $t('Timescale:') }}
                      </div>
                      <div class="details__description">
                        {{ getTimescaleTranslation(lead.enquiry.question_answers) }}
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col class="col-4 phone d-flex justify-content-end align-items-end align-items-center">
                  <a class="btn btn-sm btn-primary" :href="'tel:'+lead.enquiry.telephone">
                    <font-awesome-icon :icon="'phone'" rotation="90" class="mr-2" />{{ $t('CALL') }}
                  </a>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="mt-3">
                  <strong>{{ $t('Qualification notes:') }}</strong> {{ lead.enquiry.description }}
                </b-col>
              </b-row>
              <b-row>
                <b-col class="col-12 text-right mt-3">
                  <a class="font-weight-bold text-link" @click="openLead(lead)">
                    {{ $t('Full view') }} <font-awesome-icon icon="angle-right" />
                  </a>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </div>
      </template>
      <b-alert v-else variant="info">
        {{ $t('You do not have any leads') }}
      </b-alert>
      <div class="row">
        <div class="col-12 col-md-9 col-lg-8 m-auto d-flex justify-content-between">
          <b-select v-if="items > 20" v-model="pageSize" :options="pageSizeOptions" class="w-auto page-size" />
          <b-pagination
            v-if="hasLeads && items/pageSize > 1"
            v-model="page"
            :total-rows="items"
            :per-page="pageSize"
            aria-controls="my-leads-table"
            align="right"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Spinner from "@/components/animation/Spinner";
import LeadStatusIcons from "@/components/list/LeadStatusIcons";
import LeadShortAddress from "@/imports/mixins/leadShortAddress";
import Timescale from "@/imports/mixins/timescale";
import {MY_LEADS_RESULT_COUNT} from "@/store/mutation-types";
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";

export default {
  name: "MyLeadsList",
  components: {LeadStatusIcons, Spinner},
  mixins:[LeadShortAddress, Timescale, company, workTypeTranslation],
  data: function () {
    return {
      busy: false,
      leads: [],
      items: 0,
      filterKey: 'myLeads',
    }
  },
  computed: {
    page: {
      get: function() {
        return this.$route.query.page ?? 1;
      },
      set: function(value) {
        if ((this.$route.query.page && this.$route.query.page !== value) || (this.$route.query.page === undefined && value !== 1)) {
          this.$router.push({name:'my-leads', query:{...this.$route.query, page:value}});
        }
      },
    },
    pagePreSelect: function() {
      return this.$route.params.pagination;
    },
    hasLeads: function() {
      return this.leads.length > 0;
    },
    filters: function() {
      return {...this.$store.getters.getFilter(this.filterKey), archived: this.$store.state.leads.SHOW_ARCHIVED_LEADS};
    },
    pageSize: {
      get: function() {
        return this.$store.state.MY_LEADS_RESULT_COUNT;
      },
      set: function(value) {
        this.$store.commit(MY_LEADS_RESULT_COUNT, value);
      }
    },
    pageSizeOptions: function() {
      return [
        {value: 10, text: this.$t('Show 10')},
        {value: 20, text: this.$t('Show 20')},
        {value: 40, text: this.$t('Show 40')},
        {value: 60, text: this.$t('Show 60')},
      ];
    }
  },
  methods: {
    getLeads: function() {
      this.busy = true;
      this.$api.leads.leads({...this.filters, page:this.page, pageSize:this.pageSize}).then((result) => {
        this.leads = result.data.data;
        this.setMeta(result.data.meta);
        this.busy = false;
        this.$emit('updated', this.items);
      })
    },
    setMeta: function(metaData) {
      this.items = metaData.totalResults;
      this.pageSize = metaData.pageSize;
    },
    getReleaseDate: function(lead) {
      return lead.accepted_date ?? lead.first_request;
    },
    openLead: function(lead) {
      this.$router.push({name:'my-lead', params: {code:lead.code, fromPage: this.page}})
    },
    workTypeTranslated: function (enquiry) {
      let commsLanguage = this.company?.communications_language?.iso_code ?? 'en';
      if (commsLanguage === 'en') {
        return enquiry.work_type_description;
      }
      let translation = enquiry.work_type.translations.find(translation => translation.language.iso_code === commsLanguage);
      return translation ? translation.description : enquiry.work_type_description;
    },
  },
  watch: {
    filters: function() {
      this.getLeads();
    },
    page: function() {
      this.getLeads();
    },
    pageSize: function() {
      this.getLeads();
    }
  },
  created: function() {
    if (this.pagePreSelect) {
      this.page = this.pagePreSelect;
    }
    this.getLeads();
  }
}
</script>

<style scoped lang="scss">
.details {
  &__type {
    width: 85px;
    float: left;
    font-weight: $font-weight-bold;
  }

  &__description {
    width: calc(100% - 85px);
    float: left;
  }
}

@include media-breakpoint-down(sm) {
  .worktype-title {
   max-width: 83%;
  }
}

@media only screen and (max-width: 330px) {
  .details {
    .col-4 {
      padding-left: 0;
    }
  }
}

.lead-card {
  @include media-breakpoint-down(lg) {
    margin: 0 auto 1rem;
  }
}

</style>
